import { Box, Typography, styled, useTheme } from '@mui/material'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { IPosition, IPositionType } from 'src/generated-types'
import { ConfigurationCard, PositionConfig } from 'src/generated-types/configure-positions'
import { IChangeType } from 'src/generated-types/project-version-changes'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { CHANGE_GLYPH } from '../../../versions/changeLabels'
import { getChangeColor } from '../../../versions/projectVersionChanges.action'
import { RevisionConfigure } from '../../../versions/useRevisionConfigure'
import { comparePositions, positionLabel } from './positionLabels'
import { buildListOrder, getRangeBetween } from './PositionListView.action'

interface PositionListViewProps {
  positions: IPosition[]
  cards: ConfigurationCard[]
  configById: Map<number, PositionConfig>
  selectedIds: Set<number>
  onPositionClick: (id: number) => void
  onAdd: (ids: number[]) => void
  // Set while a draft is open.
  revision?: RevisionConfigure
  activeType?: IPositionType
}

const PositionListView: React.FC<PositionListViewProps> = ({
  positions,
  cards,
  configById,
  selectedIds,
  onPositionClick,
  onAdd,
  revision,
  activeType,
}) => {
  const { t } = useTranslation(['position', 'projects'])
  const theme = useTheme()
  const lastClickedIdRef = useRef<number | null>(null)
  const changeOf = (position: IPosition) =>
    revision?.highlight ? revision.changeByName.get(position.name ?? '')?.type : undefined
  // Removed positions only exist in the published version, so they are taken from there.
  const removed = useMemo(
    () =>
      revision?.view === 'after'
        ? revision.basePositions
            .filter(p => p.type === activeType && revision.removedByName.has(p.name ?? ''))
            .sort(comparePositions)
        : [],
    [revision, activeType],
  )
  const unconfigured = useMemo(
    () => [...positions].sort(comparePositions).filter(position => !configById.has(position.id)),
    [positions, configById],
  )
  const sortedCards = useMemo(
    () => cards.map(card => ({ ...card, positions: [...card.positions].sort(comparePositions) })),
    [cards],
  )
  const listOrder = useMemo(() => buildListOrder(unconfigured, sortedCards), [unconfigured, sortedCards])

  const onChipClick = (event: React.MouseEvent, positionId: number) => {
    const range = event.shiftKey ? getRangeBetween(listOrder, lastClickedIdRef.current, positionId) : null
    lastClickedIdRef.current = positionId
    if (range) onAdd(range)
    else onPositionClick(positionId)
  }

  const renderChip = (position: IPosition) => {
    const isSelected = selectedIds.has(position.id)
    const change = changeOf(position)
    return (
      <PositionChip
        key={position.id}
        type="button"
        aria-pressed={isSelected}
        selected={isSelected}
        accentColor={configById.get(position.id)?.color}
        onClick={event => onChipClick(event, position.id)}
      >
        {isSelected ? '✓ ' : ''}
        {positionLabel(position)}
        {change != null && (
          <ChangeGlyph sx={{ backgroundColor: getChangeColor(theme, change) }}>{CHANGE_GLYPH[change]}</ChangeGlyph>
        )}
      </PositionChip>
    )
  }

  return (
    <ListRoot>
      {unconfigured.length > 0 && (
        <Box>
          <SectionTitle>
            {t('unconfigured', { ns: 'projects' })} <SectionCount>{unconfigured.length}</SectionCount>
          </SectionTitle>
          <ChipWrap>{unconfigured.map(renderChip)}</ChipWrap>
        </Box>
      )}
      {sortedCards.map(card => (
        <Box key={card.key}>
          <SectionTitle>
            {card.drawingNumber}
            <SectionSubtitle>{card.groupName || t('configure-not-grouped', { ns: 'position' })}</SectionSubtitle>
            <SectionCount>{card.positions.length}</SectionCount>
          </SectionTitle>
          <ChipWrap>{card.positions.map(renderChip)}</ChipWrap>
        </Box>
      ))}
      {revision && removed.length > 0 && (
        <Box>
          <SectionTitle>
            {t('revision-removed-title', { ns: 'projects' })} <SectionCount>{removed.length}</SectionCount>
          </SectionTitle>
          <ChipWrap>
            {removed.map(position => {
              const picked = revision.selectedRemoved.includes(position.name ?? '')
              const color = getChangeColor(theme, IChangeType.Removed)
              return (
                <PositionChip
                  key={position.id}
                  type="button"
                  aria-pressed={picked}
                  selected={picked}
                  accentColor={color}
                  onClick={() => revision.toggleRemoved(position.name ?? '')}
                  sx={{ borderStyle: 'dashed', textDecoration: picked ? undefined : 'line-through' }}
                >
                  {picked ? '✓ ' : ''}
                  {positionLabel(position)}
                  <ChangeGlyph sx={{ backgroundColor: color }}>{CHANGE_GLYPH[IChangeType.Removed]}</ChangeGlyph>
                </PositionChip>
              )
            })}
          </ChipWrap>
        </Box>
      )}
    </ListRoot>
  )
}

export default PositionListView

const ListRoot = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}))

const SectionSubtitle = styled('span')(({ theme }) => ({
  fontWeight: 500,
  fontSize: 12,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))

const SectionCount = styled('small')(({ theme }) => ({
  fontWeight: 500,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))

const ChipWrap = styled(Box)(({ theme }) => ({ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }))

const ChangeGlyph = styled('span')({
  display: 'inline-grid',
  placeItems: 'center',
  width: 16,
  height: 16,
  borderRadius: '50%',
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
})

const PositionChip = styled('button', {
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'accentColor',
})<{ selected: boolean; accentColor?: string }>(({ theme, selected, accentColor }) => {
  const primary = getThemeColor(theme, MUIThemeColors.primaryMain)
  const line = getThemeColor(theme, MUIThemeColors.secondaryLight)
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    minWidth: 60,
    padding: theme.spacing(0.75, 1.25),
    border: `1px solid ${selected ? primary : line}`,
    borderLeftWidth: 4,
    borderLeftColor: selected ? primary : accentColor || line,
    borderRadius: 4,
    backgroundColor: selected ? primary : getThemeColor(theme, MUIThemeColors.backgroundMain),
    color: selected ? getThemeColor(theme, MUIThemeColors.white) : primary,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
  }
})
